import dayjs from 'dayjs';

import {AzureBaseMap} from "../AzureBaseMap";
import {Render} from "./Render";
import {InitMapParams} from "@esWebAPI/es/model/Models";
import {ILayerView, IScheduleLayerView, LayerInfo} from "@esWebAPI/es/model/Layer";
import {InCriteria} from "@esWebAPI/es/model/Criteria";
import _ from "lodash";
import {ScheduleModule} from '../ScheduleModule';
import {AzureOverlay} from "@esWebAPI/es/map/module/AzureOverlay";
import {LineLayer} from "@esWebAPI/es/map/layer/LineLayer";
import {RouteLayer} from "@esWebAPI/es/map/layer/route/RouteLayer";
import {IMapLayer} from "@esWebAPI/es/map/layer/MapLayer";
import {ThematicStylerFactory} from "@esWebAPI/es/map/styler/ThematicStylerFactory";
import {Service} from "@esWebAPI/es/model/Schedule";

type ViewModeType = "ALL" | "ONLY_SHOW_DAY";

export class ScheduleRender extends Render {

    private readonly _schedule: ScheduleModule;

    private viewMode: ViewModeType = "ALL";
    private viewDayTimestampInSeconds: number;

    constructor(
        baseMap: AzureBaseMap,
        featureViewModel: any,
        schedule: ScheduleModule,
        initMapParams: InitMapParams,
        overlayModule: AzureOverlay,
        stylerFactory: ThematicStylerFactory,
        eventChannel: any
    ) {

        super(baseMap, featureViewModel, undefined, initMapParams, overlayModule, stylerFactory, eventChannel);
        this._schedule = schedule;
        this.layerGroupSorter = this.sortLineLayers.bind(this);
        this.viewDayTimestampInSeconds =  0;

        this._loadComplete.then(() => {
            this._schedule.getEventChannel().addListener("onTimeFrameChange", () => {
                this.refresh();
            });
            es.api.getEventChannel().addListener("onUpdateScheduleSettings", () => {
                this.refresh();
            });
        });
    }

    private sortLineLayers(layerA: IMapLayer, layerB: IMapLayer): number {
        if (layerA.layerView.$type === "ScheduleLayerView" && layerB.layerView.$type === "ScheduleLayerView") {
            if (esApp.map.schedule.isShiftActive((layerA.layerView as IScheduleLayerView).shiftId)) {
                return -1;
            } else {
                return 1;
            }
        }
        return 0;
    }

    public async addLayer(layerView: any) {
        const layerInfo = this.featureViewModel.getLayerById(layerView.id).getLayerInfo();

        if (layerView.$type === "ScheduleLayerView") {
            this.addLineLayer(layerView, layerInfo);
        } else if (layerView.geomType === "POLYGON") {
            this.addPolygonLayer(layerView, layerInfo);
        }
        else {
            this.addPointLayer(layerView, layerInfo);
        }
    }

    protected addLineLayer(layerView: ILayerView, layerInfo: LayerInfo) {
        this.baseMap.addLayer(new LineLayer(this._map, layerView, layerInfo,
            this.baseMap.getDatasourceManager(), this.stylerFactory, this.eventChannel));
    }

    private addPointLayer(layerView: ILayerView, layerInfo: LayerInfo) {
        this.baseMap.addLayer(new RouteLayer(this._map, layerView, layerInfo, this.baseMap.getDatasourceManager(),
            this.spriteManager, this.stylerFactory, this._schedule, undefined, this.eventChannel));
    }

    private isScheduleLayer(layer: IMapLayer | undefined): layer is RouteLayer {
        return layer !== undefined && ["DefaultLayerView", "MapScopeCustomPointLayerView", "TerritoryLayerView"].includes(layer.layerView.$type) &&
            layer.layerView.styleSettings.$type === "SchedulePointStyleSettings";
    }

    private isScheduleLineLayer(layer: IMapLayer | undefined): layer is LineLayer {
        return layer !== undefined && layer.layerView.$type === "ScheduleLayerView";
    }

    private showUnscheduledStops() {
        this.baseMap.layers
            .filter(l => this.isScheduleLayer(l))
            .forEach(l => {
                const scheduleLayer = (l as RouteLayer);
                scheduleLayer.showUnscheduledPoints();
                scheduleLayer.showAllStops();
            });
    }

    public async onlyShowPointsAndRouteForDay(dayTimestampInSeconds: number) {

        const day = dayjs.unix(dayTimestampInSeconds);
        const start = day.startOf("day").unix();
        const end = day.endOf("day").unix();
        this._schedule.setTimeFrame([start*1000, end*1000], true);

        const pointLayers: RouteLayer[] = this.baseMap.layers
            .filter( l => this.isScheduleLayer(l)) as RouteLayer[];

        const promises = [];
        for (let layer of pointLayers){
            promises.push(layer.showPointsScheduledForDayOnly(start, end));
        }

        await Promise.allSettled(promises);

        const lineLayers: LineLayer[] = this.baseMap.layers
            .filter(l => this.isScheduleLineLayer(l)) as LineLayer[];

        const shiftId = this._schedule.getShiftByObjRef(dayjs.unix(dayTimestampInSeconds).toDate())?.shiftId ?? null;
        for (let layer of lineLayers) {
            await layer.update({});
            if((layer.layerView as IScheduleLayerView).shiftId === shiftId) {
                layer.show();
            } else {
                layer.hide();
            }
        }
    }

    showAllRouteLines() {
        const lineLayers: LineLayer[] = this.baseMap.layers.filter(l => this.isScheduleLineLayer(l)) as LineLayer[]
        lineLayers.forEach(l => l.show());
    }

    selectFeature(layerViewId: number, featureId: number) {
        const layer = this.baseMap.getLayersByLayerViewId(layerViewId)[0];
        if (layer && this.isScheduleLayer(layer)) {
            (layer as RouteLayer).selectFeature(featureId)
        }
    }

    /**
     * Automatically zoom to the active data
     */
    autoZoom() {

        const activeShiftIds = this._schedule.getShiftsObjRef().filter((s: any) =>
            this._schedule.isShiftActive(s.shiftId)
        ).map((s: any) => s.shiftId);

        //1. Group services by layerViewId
        const services = this._schedule.getServicesClone()
            .filter((s: Service) => {
                const shiftId = this._schedule.util.getShiftFromService(s)?.shiftId;
                return !this._schedule.isServiceBreak(s) && this._schedule.isServiceScheduledInSolution(s) && shiftId && activeShiftIds.includes(shiftId);
            });

        const byLayerViewId: {[layerViewId: number]: number[]} = {};
        services.forEach((s: Service) => {
            if(!byLayerViewId.hasOwnProperty(s.layerViewId)){
                byLayerViewId[s.layerViewId] = [];
            }
            byLayerViewId[s.layerViewId].push(s.featureId);
        })

        if(!services.length){
            const datasetLvIds = this.featureViewModel.listLayers()
                .filter((lvm:any) => lvm.getType() === "MapScopeCustomPointLayerView" || lvm.getType() === "DefaultLayerView")
                .map((lvm: any) => lvm.getId());

            this.baseMap.zoomToLayerViewExtents(datasetLvIds);
            return;
        }


        //2. Get bounds for services in each layerView
        const dfds: any[] = [];
        _.keys(byLayerViewId).forEach((layerViewId: string) => {
            const criteria: InCriteria<number> = {"$type": "In", attr: "ES_ID", values: byLayerViewId[+layerViewId]};
            dfds.push(es.api.feature.bounds(+layerViewId, criteria));
        })


        //3. Merge bounds and zoomTo
        Promise.all(dfds).then((layersBounds: number[][]) => {


            let bounds: number[] = [];
            layersBounds.forEach((layerBounds: number[], i: number) => {
                if (i == 0) {
                    bounds = layerBounds
                } else {
                    es.util.bounds.union(layerBounds, bounds, bounds);
                }
            });
            this.baseMap.setBounds(bounds, 10, true);
        });
    }

    public async setViewMode(viewMode: ViewModeType, dayTimestampInSeconds?: number) {
        this.viewMode = viewMode;
        this.viewDayTimestampInSeconds = dayTimestampInSeconds ?? this._schedule.getTimeFrame()[0] / 1000;
        if(viewMode === "ALL"){
            this.showAllRouteLines();
            this.showUnscheduledStops();
            return
        }

        await this.onlyShowPointsAndRouteForDay(this.viewDayTimestampInSeconds);
    }


    public async refresh(): Promise<void> {
        //TODO FIX ME Mobile App is sometimes sending refresh twice after a optimise day.
        if (this._isRefreshing) {
            return Promise.resolve();
        }
        await super.refresh();
        await this._loadComplete;
        await this.setViewMode(this.viewMode, this.viewDayTimestampInSeconds);

    }
}
